import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Table from '../../components/common/Table';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import Pagination from '../../components/common/Pagination';

interface MasterItem {
  id: string;
  name: string;
  code: string;
  displayOrder?: number;
  isActive?: boolean;
  createdAt: string;
  [key: string]: unknown;
}

const masterLabels: Record<string, string> = {
  areas: 'エリア',
  categories: 'カテゴリ',
  statuses: 'ステータス',
  departments: '部署',
  positions: '役職',
  clientTypes: '取引先種別',
  products: '商品（商材）',
  powerCompanies: '電力会社',
  constructionPartners: '施工パートナー',
  demandLocations: '需要場所',
  contractTypes: '契約種別',
  constructionTypes: '工事種別',
  facilityTypes: '設備種別',
};

export default function MasterList() {
  const { masterType = '' } = useParams<{ masterType: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<MasterItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<MasterItem | null>(null);
  const [formData, setFormData] = useState({ name: '', code: '', displayOrder: 0 });
  const [saving, setSaving] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  
  // ページネーション・ソート・表示件数（URL同期）
  const currentPage = parseInt(searchParams.get('page') || '1');
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: searchParams.get('sortBy') || 'displayOrder',
    direction: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'asc',
  });
  const setCurrentPage = (page: number | ((prev: number) => number)) => {
    const p = typeof page === 'function' ? page(currentPage) : page;
    setSearchParams(prev => { prev.set('page', String(p)); return prev; }, { replace: true });
  };
  const setPageSize = (size: number) => {
    setSearchParams(prev => { prev.set('pageSize', String(size)); prev.set('page', '1'); return prev; }, { replace: true });
  };

  const masterLabel = masterLabels[masterType] || masterType;

  useEffect(() => {
    setSearchParams(prev => { prev.set('page', '1'); return prev; }, { replace: true });
  }, [masterType]);

  useEffect(() => {
    fetchItems();
  }, [masterType, currentPage, pageSize, sortConfig]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('pageSize', pageSize.toString());
      params.append('sortBy', sortConfig.key);
      params.append('sortOrder', sortConfig.direction);
      
      const data = await api.get<{ items: MasterItem[]; total: number; totalPages: number }>(`/api/masters/${masterType}?${params}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotalPages(data?.totalPages ?? 1);
    } catch (error) {
      console.error('Failed to fetch master items:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const defaultSort = { key: 'createdAt', direction: 'desc' as const };
  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      let next: { key: string; direction: 'asc' | 'desc' };
      if (prev.key !== key) next = { key, direction: 'asc' };
      else if (prev.direction === 'asc') next = { key, direction: 'desc' };
      else next = defaultSort;
      setSearchParams(p => { p.set('sortBy', next.key); p.set('sortOrder', next.direction); p.set('page', '1'); return p; }, { replace: true });
      return next;
    });
  };

  const handleEdit = (item: MasterItem) => {
    setEditItem(item);
    setFormData({ name: item.name, code: item.code, displayOrder: item.displayOrder || 0 });
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditItem(null);
    setFormData({ name: '', code: '', displayOrder: 0 });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/api/masters/${masterType}/${editItem.id}`, formData);
      } else {
        await api.post(`/api/masters/${masterType}`, formData);
      }
      setShowModal(false);
      fetchItems();
    } catch (error) {
      console.error('Failed to save master item:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await api.delete(`/api/masters/${masterType}/${deleteTargetId}`);
      setDeleteTargetId(null);
      fetchItems();
    } catch (error) {
      console.error('Failed to delete master item:', error);
      toast.error('削除に失敗しました');
    }
  };

  const columns = [
    { key: 'code', header: 'コード', minWidth: '100px', sortable: true, hideOnMobile: true },
    { key: 'name', header: '名称', minWidth: '150px', sortable: true, mobileTitle: true },
    { key: 'displayOrder', header: '表示順', minWidth: '70px', sortable: true, hideOnMobile: true },
    { key: 'isActive', header: 'ステータス', minWidth: '90px', sortable: true, render: (item: MasterItem) => (
      <Badge variant={item.isActive !== false ? 'success' : 'default'}>
        {item.isActive !== false ? '有効' : '無効'}
      </Badge>
    )},
    { key: 'actions', header: '', minWidth: '100px', render: (item: MasterItem) => (
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} className="px-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          <span className="hidden lg:inline ml-1">編集</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setDeleteTargetId(item.id)} className="px-2 text-red-500">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          <span className="hidden lg:inline ml-1">削除</span>
        </Button>
      </div>
    )},
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{masterLabel}マスタ</h1>
        <Button onClick={handleCreate} className="w-full sm:w-auto">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          新規追加
        </Button>
      </div>

      <Card>
        <Table 
          columns={columns} 
          data={items} 
          loading={loading} 
          emptyMessage={`${masterLabel}データがありません`}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        
        {/* ページネーション */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 order-2 sm:order-1">
            <span className="hidden sm:inline">表示件数:</span>
            <Select
              value={pageSize.toString()}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="w-20 sm:w-24 text-sm"
            >
              <option value="10">10件</option>
              <option value="30">30件</option>
              <option value="50">50件</option>
              <option value="100">100件</option>
            </Select>
          </div>
          <div className="order-1 sm:order-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </Card>

      <Modal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        title={editItem ? `${masterLabel}を編集` : `${masterLabel}を追加`}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="コード"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            disabled={!!editItem}
            required
          />
          <Input
            label="名称"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="表示順"
            type="number"
            value={formData.displayOrder}
            onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
          />
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)} className="w-full sm:w-auto">
              キャンセル
            </Button>
            <Button type="submit" loading={saving} className="w-full sm:w-auto">
              {editItem ? '更新' : '追加'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        title={`${masterLabel}の削除`}
      >
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">本当に削除しますか？</p>
          <p className="text-sm text-red-600">この操作は取り消せません。</p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setDeleteTargetId(null)}>
              キャンセル
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              削除する
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
